import { Heart } from "lucide-react";
import { type QuizConfig } from "~/types/quiz";

export const fiveLoveLanguages: QuizConfig = {
  id: "5-jezika-ljubavi",
  illustration: {
    src: "/images/quizzes/5-jezika-ljubavi.webp",
    alt: "Partneri razmenjuju male gestove pažnje uz šolju toplog napitka.",
  },
  lastModified: "2026-10-05",
  title: "Koji je tvoj jezik ljubavi?",
  shortTitle: "Jezici ljubavi",
  description:
    "Otkrij kako najradije daješ i primaš ljubav kroz pet osnovnih jezika ljubavi.",
  longDescription: `Koncept "jezika ljubavi" stvorio je bračni savetnik dr Gary Chapman. On je primetio da se ljudi razlikuju u tome kakve interakcije ih čine voljenim. Kada znaš svoj jezik ljubavi, bolje razumeš sebe i svog partnera, brže rešavaš konflikte i povećavaš intimnost u vezi.`,
  duration: "5-10 min",
  category: "Odnosi i komunikacija",
  focusAreasWanted: [
    "couples_issues",
    "relationships",
    "communication",
    "self_development",
  ],
  icon: Heart,
  promptExtension: `
    - Present the primary love language prominently
    - Show all five love languages with their percentages
    - Use the bucket interpretations (full/partial/no) to provide nuanced feedback
    - Explain what each love language means in practical terms
    - Provide actionable advice for communicating needs based on the results
    - Consider showing ranked results if there are multiple high-scoring languages
  `,
  questions: [
    // Each question has 2 options mapping to love language codes A, B, C, D, or E
    // We use numeric scores 0-4 to represent A-E:
    // 0 = A (words_of_affirmation), 1 = B (quality_time), 2 = C (receiving_gifts),
    // 3 = D (acts_of_service), 4 = E (physical_touch)
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Volim kada dobijem podsticajne ili ljubavne poruke bez posebnog razloga.",
        "Volim kada me zagrle.",
      ],
      scores: [0, 4], // A, E
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Volim da provodim vreme sam na sam sa osobom koja mi je posebna.",
        "Osećam se voljeno kada mi neko pruži praktičnu pomoć.",
      ],
      scores: [1, 3], // B, D
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Volim kada mi ljudi daju poklone.",
        "Volim opuštene posete sa prijateljima i voljenima.",
      ],
      scores: [2, 1], // C, B
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Osećam se voljeno kada ljudi rade stvari koje mi pomažu, poput pranja veša ili kuvanja obroka.",
        "Osećam se voljeno kada mi ljudi daju podržavajuće zagrljaje ili tapšu me po ramenu.",
      ],
      scores: [3, 4], // D, E
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Osećam se voljeno kada osoba koju volim ili poštujem stavi ruku oko mene.",
        "Osećam se voljeno kada dobijem poklon od nekoga ko mi je važan.",
      ],
      scores: [4, 2], // E, C
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Volim da idem na mesta sa prijateljima ili voljenima.",
        "Volim da se rukujem, da dam 'high five' ili držim za ruku osobe koje su mi posebne.",
      ],
      scores: [1, 4], // B, E
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Vidljivi simboli ljubavi (kao pokloni) su mi važni.",
        "Osećam se voljeno kada mi ljudi kažu ljubazne ili podržavajuće stvari.",
      ],
      scores: [2, 0], // C, A
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Volim da sedim blizu osoba sa kojima uživam.",
        "Volim kada mi ljudi kažu da sam privlačan/na ili lep/a.",
      ],
      scores: [4, 0], // E, A
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Volim da provodim vreme sa prijateljima i voljenima.",
        "Volim da primam poklone od prijatelja i voljenih.",
      ],
      scores: [1, 2], // B, C
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Reči prihvatanja su mi važne.",
        "Znam da me neko voli kada mi pomogne.",
      ],
      scores: [0, 3], // A, D
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Volim da budem zajedno i da radim stvari sa prijateljima i voljenima.",
        "Volim kada mi se upućuju ljubazne reči.",
      ],
      scores: [1, 0], // B, A
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Ono što neko radi utiče na mene više nego ono što kaže.",
        "Zagrljaji me čine povezanim i cenjenim.",
      ],
      scores: [3, 4], // D, E
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Cenim pohvale i trudim se da izbegnem kritiku.",
        "Nekoliko malih poklona mi znači više nego jedan veliki poklon.",
      ],
      scores: [0, 2], // A, C
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Osećam bliskost prema nekome kada razgovaramo ili radimo nešto zajedno.",
        "Osećam se bliže prijateljima i voljenima kada se zagrlimo ili rukujemo.",
      ],
      scores: [1, 4], // B, E
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Volim kada ljudi pohvaljuju moja dostignuća.",
        "Znam da me ljudi vole kada rade stvari koje oni ne vole, a radi mene.",
      ],
      scores: [0, 3], // A, D
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Volim fizički dodir prijatelja i voljenih kada sam u njihovoj blizini.",
        "Volim kada me ljudi slušaju i pokazuju iskreno interesovanje za ono što govorim.",
      ],
      scores: [4, 1], // E, B
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Osećam se voljeno kada mi prijatelji i voljeni pomognu sa poslovima ili projektima.",
        "Zaista uživam kada primam poklone od prijatelja i voljenih.",
      ],
      scores: [3, 2], // D, C
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Volim kada ljudi pohvaljaju moj izgled.",
        "Osećam se voljeno kada ljudi odvoje vreme da razumeju moja osećanja.",
      ],
      scores: [0, 1], // A, B
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Osećam se sigurno kada je posebna osoba fizički blizu mene.",
        "Dela ljubavi me čine voljenim.",
      ],
      scores: [4, 3], // E, D
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Cenim mnoge stvari koje posebne osobe rade za mene.",
        "Volim da primam poklone koje posebne osobe prave za mene.",
      ],
      scores: [3, 2], // D, C
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Zaista uživam u osećaju kada mi neko pokloni punu pažnju.",
        "Zaista uživam u osećaju kada neko uradi neku uslugu za mene.",
      ],
      scores: [1, 3], // B, D
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Volim kada osoba proslavi moj rođendan sa poklonom.",
        "Volim kada mi osoba kaže smislene reči na moj rođendan.",
      ],
      scores: [2, 0], // C, A
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Znam da osoba misli na mene kada mi daje poklon.",
        "Osećam se voljeno kada mi osoba pomogne sa mojim obavezama ili zadacima.",
      ],
      scores: [2, 3], // C, D
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Cenim kada neko strpljivo sluša i ne prekida me.",
        "Cenim kada neko pamti posebne dane sa poklonom.",
      ],
      scores: [1, 2], // B, C
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Volim da znam da su voljeni dovoljno zabrinuti da mi pomognu sa svakodnevnim zadacima.",
        "Uživam u produženim putovanjima sa nekim ko mi je poseban.",
      ],
      scores: [3, 1], // D, B
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Ne smeta mi fizička nežnost od prijatelja sa kojima sam blizak/a.",
        "Primanje poklona bez posebnog razloga me oduševljava.",
      ],
      scores: [4, 2], // E, C
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Volim kada mi se kaže da sam cenjen/a.",
        "Volim kada me osoba pogleda dok razgovaramo.",
      ],
      scores: [0, 1], // A, B
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Pokloni prijatelja ili voljene osobe su uvek posebni za mene.",
        "Osećam se dobro kada me prijatelj ili voljena osoba zagrli ili dodirne.",
      ],
      scores: [2, 4], // C, E
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Osećam se voljeno kada osoba entuzijastično uradi neki zadatak koji sam zatražio/la.",
        "Osećam se voljeno kada mi se kaže koliko sam cenjen/a.",
      ],
      scores: [3, 0], // D, A
    },
    {
      type: "radio",
      question: "Odaberi rečenicu koja je važnija za tebe",
      options: [
        "Potreban mi je fizički kontakt sa ljudima svaki dan.",
        "Potrebne su mi reči ohrabrenja i potvrđivanja svaki dan.",
      ],
      scores: [4, 0], // E, A
    },
    {
      type: "textarea",
      question:
        "Opiši kako voliš da ti partner pokaže ljubav, ili kako ti najčešće pokazuješ ljubav:",
    },
  ],
  copy: {
    title: "Tvoj jezika ljubavi",
    subtitle:
      "Otkrij kako najradije daješ i primaš ljubav kroz pet osnovnih jezika ljubavi",
    scoreLabel: "Tvoji rezultati po jezicima ljubavi:",
    adviceTitle: "Tvoj profil jezika ljubavi",
    resourcesTitle: "Preporučujemo da pročitaš:",
    whatNextTitle: "Šta dalje?",
    shareButtonText: "Podeli rezultat",
  },
};
